import React from "react";

// Minimal SVG icon set — all 24×24 stroke icons so colour and size come
// from the parent via `currentColor` and font-size / Tailwind width.
// Keeping them inline here avoids pulling in a 400kB icon package when the
// chat UI only uses a dozen glyphs.

interface IconProps extends React.SVGAttributes<SVGSVGElement> {
  size?: number | string;
}

function base({ size = 16, strokeWidth = 1.8, ...rest }: IconProps) {
  return {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true as const,
    ...rest,
  };
}

export const PlusIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const SearchIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

export const SendIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M12 19V5M5 12l7-7 7 7" />
  </svg>
);

export const MessageIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

export const DashboardIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <rect x="3" y="3" width="7" height="9" rx="1.5" />
    <rect x="14" y="3" width="7" height="5" rx="1.5" />
    <rect x="14" y="12" width="7" height="9" rx="1.5" />
    <rect x="3" y="16" width="7" height="5" rx="1.5" />
  </svg>
);

export const SparkleIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M12 3v4M12 17v4M4 12H2M22 12h-2M6.3 6.3 4.9 4.9M19.1 19.1l-1.4-1.4M6.3 17.7l-1.4 1.4M19.1 4.9l-1.4 1.4" />
  </svg>
);

export const SignOutIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M15 12H3m0 0 4-4m-4 4 4 4" />
    <path d="M9 4h7a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H9" />
  </svg>
);

export const ArrowUpIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M12 19V5M5 12l7-7 7 7" />
  </svg>
);

export const ChatBubbleIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 6.7 15l1.3 3.5-3.5-1.3A9 9 0 0 1 3 12z" />
  </svg>
);

export const BoltIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" />
  </svg>
);

export const UsersIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

export const TrendUpIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="m22 7-8.5 8.5-5-5L2 17" />
    <path d="M16 7h6v6" />
  </svg>
);

export const TrendDownIcon: React.FC<IconProps> = (p) => (
  <svg {...base(p)}>
    <path d="m22 17-8.5-8.5-5 5L2 7" />
    <path d="M16 17h6v-6" />
  </svg>
);

export const SpinnerIcon: React.FC<IconProps> = ({ size = 16, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    {...rest}
  >
    <circle
      cx="12"
      cy="12"
      r="9"
      stroke="currentColor"
      strokeOpacity="0.25"
      strokeWidth="2.5"
    />
    <path
      d="M21 12a9 9 0 0 0-9-9"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    >
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="0 12 12"
        to="360 12 12"
        dur="0.9s"
        repeatCount="indefinite"
      />
    </path>
  </svg>
);
